import apiAjaxOptions from './apiAjaxOptions';
import ApeFetch from './apeFetch';
import sendToNotifier from './errorNotifiers';
import { t } from 'i18next';

interface ApiOptions {
  additionalHeaders: { key: string; value: string }[];
  body: string;
  contentType: string;
  responseOnly: boolean;
  cacheRequest: boolean;
}

// limit api calls to 50 calls in a second. If we get more then throw them out to an error page.
let requests = 0;
const timePeriod = 1000;
const rateLimit = 200;
let addressesCalled: string[] = [];

setInterval(function() {
  requests = 0;
  addressesCalled = [];
}, timePeriod);

export function apiCallWithOptions(
  address: string,
  method: string,
  options: ApiOptions
) {
  options = options || {};
  let { additionalHeaders, body, contentType, responseOnly } = options;
  let promise = callTheAPI(
    address,
    method,
    additionalHeaders,
    body,
    contentType,
    responseOnly
  );
  return promise;
}

async function handleStatusCodes(res: Response) {
  if (res.status === 504) {
    // Gateway timeout
    throw new Error(
      'The request timed out. Check if it has succeeded and if not then try again later.'
    );
  } else if (res.status > 499) {
    // All other non-successful status codes should throw the generic error message
    throw new Error(
      t(
        'Sorry, something went wrong processing your request. Please contact Damstra Forms Support.'
      )
    );
  } else if (res.status === 429) {
    // Too many requests
    throw new Error('Please try again in five minutes');
  } else if (res.ok === false) {
    // For status codes 300-499, the server will usually respond with an error message.
    // Fall back to the generic error message if nothing is found
    let serverError;
    try {
      const json = await res.json();
      serverError =
        json.error_message ||
        json.error ||
        json.message ||
        t(
          'Sorry, something went wrong processing your request. Please contact Damstra Forms Support.'
        );
    } catch (err) {
      serverError = t(
        'Sorry, something went wrong processing your request. Please contact Damstra Forms Support.'
      );
    }
    throw new Error(serverError);
  }
}

export default async function callTheAPI<T = undefined>(
  apiAddress: string,
  method: string = 'GET',
  additionalHeaders?: { key: string; value: string }[] | null,
  body?: string | null,
  contentType?: string,
  responseOnly?: boolean
): Promise<T> {
  requests++;
  checkRate(apiAddress);
  const options = apiAjaxOptions(method, additionalHeaders, body, contentType);

  try {
    let data;
    const res = await ApeFetch.fetch(apiAddress, options);
    await handleStatusCodes(res);
    if (responseOnly === true) {
      // TODO: Replace uses of this with `fetchResponseOnly()`
      //@ts-ignore
      return res;
    } else if (res.status !== 204) {
      data = await res.json();
    }
    return data;
  } catch (err) {
    throw new Error(handleError(err));
  }
}

export async function fetchResponseOnly(
  apiAddress: string,
  method: string = 'GET',
  additionalHeaders?: { key: string; value: string }[] | null,
  body?: string | null,
  contentType?: string,
  responseOnly?: boolean
): Promise<Response> {
  requests++;
  checkRate(apiAddress);
  const options = apiAjaxOptions(method, additionalHeaders, body, contentType);
  try {
    const res = await ApeFetch.fetch(apiAddress, options);
    return res;
  } catch (err) {
    throw new Error(handleError(err));
  }
}

function handleError(err: any) {
  if ((err as Error).message === 'Failed to fetch') {
    // Fetch only throws on network errors, but we still want a meaningful message
    return Error(t('It appears that your internet connection is down.'));
  }
  // Not a network error, so rethrow whatever we got
  return err;
}

export function checkRate(address: string) {
  addressesCalled.push(address);
  if (requests >= rateLimit) {
    // Send a notification to airbrake with the address we were hitting
    const errorTitle =
      'JS Client calling API exceeded the permitted rate limit.';
    const errorMessage = addressesCalled.join(',');

    sendToNotifier(errorTitle, errorMessage);
    window.location.href = '/oops';
  }
}
